<template>
  <VDialog
    :model-value="isDialogVisible"
    max-width="600"
    scrollable
    @update:model-value="updateModelValue"
  >
    <VCard class="dialog-card">
      <template v-if="formSubmitted">
        <!-- Success/Confirmation State -->
        <VBtn
          icon
          size="small"
          variant="text"
          class="close-button-success"
          @click="onClose"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>

        <VCardText class="pa-10 text-center">
          <div class="mt-8 text-lg font-weight-medium">
            <template v-if="noneSelected">
              Thank you for your request to add {{ formData.name }}!
            </template>
            <template v-else>
              Thanks for adding {{ selectedPlace?.name || formData.name }} as a favorite!
            </template>
          </div>
          <div
            v-if="noneSelected"
            class="mt-4 text-base"
          >
            You'll receive an email from us within 24 hours.
          </div>
        </VCardText>

        <VCardActions class="pa-6 pt-0 justify-center">
          <VBtn
            color="primary"
            variant="elevated"
            size="large"
            @click="onClose"
          >
            Close
          </VBtn>
        </VCardActions>
      </template>

      <template v-else>
        <!-- Form State with Steps -->
        <VCardTitle class="dialog-header pa-6 text-center position-relative">
          <div class="d-flex align-center justify-center w-100">
            <VBtn
              v-if="currentStep > 1"
              icon
              size="small"
              variant="text"
              class="back-button"
              :disabled="isSubmitting || isSearching"
              @click="goBack"
            >
              <VIcon>mdi-arrow-left</VIcon>
            </VBtn>
            <span class="dialog-title">{{ stepTitle }}</span>
          </div>
          <VBtn
            icon
            size="small"
            variant="text"
            class="close-button"
            :disabled="isSubmitting"
            @click="onCancel"
          >
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </VCardTitle>

        <!-- Step indicator -->
        <div class="step-indicator pa-4 pb-0">
          <div class="step-dots">
            <span
              v-for="step in 3"
              :key="step"
              class="step-dot"
              :class="{ active: step === currentStep, completed: step < currentStep }"
            />
          </div>
        </div>

        <VCardText class="pa-6 dialog-content">
          <div class="steps-container">
            <Transition
              :name="transitionName"
              mode="out-in"
            >
              <!-- Step 1: Restaurant Name and City -->
              <div
                v-if="currentStep === 1"
                key="step1"
                class="step-content"
              >
                <VForm
                  ref="step1FormRef"
                  @submit.prevent="goToStep2"
                >
                  <VTextField
                    v-model="formData.name"
                    label="Restaurant Name"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="comfortable"
                    class="mb-4"
                    hide-details="auto"
                    autofocus
                  />

                  <VTextField
                    v-model="formData.market"
                    label="City, State/Country"
                    placeholder="e.g. Los Angeles, CA"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="comfortable"
                    class="mb-6"
                    hide-details="auto"
                  />

                  <VBtn
                    color="primary"
                    variant="elevated"
                    :loading="isSearching"
                    block
                    size="large"
                    class="submit-button"
                    @click="goToStep2"
                  >
                    Next
                  </VBtn>
                </VForm>
              </div>

              <!-- Step 2: Google Places Results -->
              <div
                v-else-if="currentStep === 2"
                key="step2"
                class="step-content"
              >
                <div class="search-info mb-4">
                  <p class="text-body-2 text-grey-darken-1">
                    We found these restaurants matching "<strong>{{ formData.name }}</strong>" in <strong>{{ formData.market }}</strong>
                  </p>
                </div>

                <!-- Loading state -->
                <div
                  v-if="isSearching"
                  class="loading-state"
                >
                  <VProgressCircular
                    indeterminate
                    color="primary"
                    size="48"
                  />
                  <p class="mt-4 text-grey">
                    Searching for restaurants...
                  </p>
                </div>

                <!-- Results list -->
                <div
                  v-else
                  class="results-container"
                >
                  <!-- New restaurants section (selectable) -->
                  <div
                    v-if="googlePlacesResults.length > 0 || existingRestaurants.length > 0"
                    class="results-list"
                  >
                    <!-- Show new restaurants if any -->
                    <div
                      v-for="(place, index) in googlePlacesResults"
                      :key="place.place_id"
                      class="place-result"
                      :class="{ selected: selectedPlace?.place_id === place.place_id }"
                      @click="selectPlace(place)"
                    >
                      <div class="place-image">
                        <img
                          v-if="place.photoUrl"
                          :src="place.photoUrl"
                          :alt="place.name"
                          @error="handleImageError($event, index)"
                        >
                        <div
                          v-else
                          class="no-image"
                        >
                          <VIcon>mdi-silverware-fork-knife</VIcon>
                        </div>
                      </div>
                      <div class="place-info">
                        <div class="place-name">
                          {{ place.name }}
                        </div>
                        <div class="place-address">
                          {{ place.formatted_address || place.vicinity }}
                        </div>
                      </div>
                      <div class="place-check">
                        <VIcon
                          v-if="selectedPlace?.place_id === place.place_id"
                          color="primary"
                        >
                          mdi-check-circle
                        </VIcon>
                        <VIcon
                          v-else
                          color="grey-lighten-1"
                        >
                          mdi-circle-outline
                        </VIcon>
                      </div>
                    </div>

                    <!-- None of these option - always show if there are any results -->
                    <div
                      class="place-result none-option"
                      :class="{ selected: selectedPlace === null && noneSelected }"
                      @click="selectNone"
                    >
                      <div class="place-image none-image">
                        <VIcon>mdi-help-circle-outline</VIcon>
                      </div>
                      <div class="place-info">
                        <div class="place-name">
                          None of these
                        </div>
                      </div>
                      <div class="place-check">
                        <VIcon
                          v-if="selectedPlace === null && noneSelected"
                          color="primary"
                        >
                          mdi-check-circle
                        </VIcon>
                        <VIcon
                          v-else
                          color="grey-lighten-1"
                        >
                          mdi-circle-outline
                        </VIcon>
                      </div>
                    </div>
                  </div>

                  <!-- Existing restaurants section (non-selectable) -->
                  <div
                    v-if="existingRestaurants.length > 0"
                    class="existing-restaurants-section"
                  >
                    <div class="section-title">
                      Restaurants already on RM
                    </div>
                    <div class="results-list">
                      <div
                        v-for="(place, index) in existingRestaurants"
                        :key="place.place_id"
                        class="place-result existing-restaurant"
                      >
                        <div class="place-image">
                          <img
                            v-if="place.photoUrl"
                            :src="place.photoUrl"
                            :alt="place.name"
                            @error="handleExistingImageError($event, index)"
                          >
                          <div
                            v-else
                            class="no-image"
                          >
                            <VIcon>mdi-silverware-fork-knife</VIcon>
                          </div>
                        </div>
                        <div class="place-info">
                          <div class="place-name">
                            {{ place.name }}
                          </div>
                          <div class="place-address">
                            {{ place.formatted_address || place.vicinity }}
                          </div>
                          <div class="place-status">
                            {{ place.dbRestaurant?.status || 'In Database' }}
                          </div>
                        </div>
                        <div class="place-check">
                          <VIcon color="success">
                            mdi-check-circle
                          </VIcon>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No results state -->
                  <div
                    v-if="googlePlacesResults.length === 0 && existingRestaurants.length === 0 && !isSearching"
                    class="no-results"
                  >
                    <VIcon
                      size="48"
                      color="grey-lighten-1"
                    >
                      mdi-map-search-outline
                    </VIcon>
                    <p class="mt-2 text-grey">
                      No restaurants found. You can still continue with your request.
                    </p>
                  </div>
                </div>

                <VBtn
                  color="primary"
                  variant="elevated"
                  :disabled="!canProceedToStep3"
                  block
                  size="large"
                  class="submit-button mt-6"
                  @click="goToStep3"
                >
                  {{ selectedPlace ? 'Select & Continue' : 'Continue Without Selection' }}
                </VBtn>
              </div>

              <!-- Step 3: Optional Fields -->
              <div
                v-else-if="currentStep === 3"
                key="step3"
                class="step-content"
              >
                <VForm
                  ref="step3FormRef"
                  @submit.prevent="onSubmit"
                >
                  <!-- Show selected restaurant if one was chosen -->
                  <div
                    v-if="selectedPlace"
                    class="selected-restaurant-preview mb-4"
                  >
                    <div class="preview-image">
                      <img
                        v-if="selectedPlace.photoUrl"
                        :src="selectedPlace.photoUrl"
                        :alt="selectedPlace.name"
                      >
                      <div
                        v-else
                        class="no-image"
                      >
                        <VIcon>mdi-silverware-fork-knife</VIcon>
                      </div>
                    </div>
                    <div class="preview-info">
                      <div class="preview-name">
                        {{ selectedPlace.name }}
                      </div>
                      <div class="preview-address">
                        {{ selectedPlace.formatted_address || selectedPlace.vicinity }}
                      </div>
                    </div>
                  </div>

                  <!-- Submit button and contact info -->
                  <VBtn
                    color="primary"
                    variant="elevated"
                    :loading="isSubmitting"
                    block
                    size="large"
                    class="submit-button mb-4"
                    @click="onSubmit"
                  >
                    {{ noneSelected ? 'Request to add' : 'Add to favorites' }}
                  </VBtn>
                  <div class="contact-text">
                    questions? email <a
                      href="mailto:preferred@reserverm.com"
                      class="email-link"
                    >preferred@reserverm.com</a>
                  </div>
                </VForm>
              </div>
            </Transition>
          </div>
        </VCardText>
      </template>
    </VCard>
  </VDialog>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRestaurantsStore, useUsersStore } from '@/stores'
import GooglePlacesService from '@/services/GooglePlacesService'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    required: true
  },
  searchQuery: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['update:isDialogVisible', 'onRestaurantAdded'])
const restaurantsStore = useRestaurantsStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const step1FormRef = ref(null)
const step3FormRef = ref(null)
const isSubmitting = ref(false)
const isSearching = ref(false)
const formSubmitted = ref(false)
const currentStep = ref(1)
const transitionName = ref('slide-left')

// Google Places state
const googlePlacesResults = ref([]) // New restaurants not in database
const existingRestaurants = ref([]) // Restaurants already in database
const selectedPlace = ref(null)
const noneSelected = ref(false)
const selectedPlaceDetails = ref(null)
const selectedPlaceTimeZone = ref(null)
const existingPlacesMap = ref({}) // Map of existing restaurant data by place_id

const formData = reactive({
  name: '',
  market: '',
})

// Rules for validation
const rules = {
  required: value => !!value || 'This field is required'
}

// Computed properties
const stepTitle = computed(() => {
  switch (currentStep.value) {
  case 1:
    return 'Add favorite'
  case 2:
    return 'Select Restaurant'
  case 3:
    return `Thanks for adding`
  default:
    return 'Request to add Favorite'
  }
})

const canProceedToStep3 = computed(() => {
  // User must select a restaurant or "None of these" if any results exist
  // Only allow proceeding without selection if there are truly no results
  const hasAnyResults = googlePlacesResults.value.length > 0 || existingRestaurants.value.length > 0
  const hasSelection = selectedPlace.value !== null || noneSelected.value

  return hasSelection || !hasAnyResults
})

// Watch for dialog visibility to reset form
watch(() => props.isDialogVisible, newVal => {
  if (newVal) {
    resetForm()
    currentStep.value = 1
    transitionName.value = 'slide-left'

    // Pre-fill name from search query if exists
    if (props.searchQuery) {
      formData.name = props.searchQuery
    }
  }
})

const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}

const resetForm = () => {
  formData.name = ''
  formData.market = ''
  formSubmitted.value = false
  googlePlacesResults.value = []
  existingRestaurants.value = []
  selectedPlace.value = null
  noneSelected.value = false
  selectedPlaceDetails.value = null
  selectedPlaceTimeZone.value = null
  existingPlacesMap.value = {}

  if (step1FormRef.value) {
    step1FormRef.value.resetValidation()
  }
  if (step3FormRef.value) {
    step3FormRef.value.resetValidation()
  }
}

const onCancel = () => {
  resetForm()
  emit('update:isDialogVisible', false)
}

const onClose = () => {
  resetForm()
  emit('update:isDialogVisible', false)
}

const goBack = () => {
  transitionName.value = 'slide-right'
  if (currentStep.value === 2) {
    currentStep.value = 1
  } else if (currentStep.value === 3) {
    currentStep.value = 2
  }
}

const goToStep2 = async () => {
  const { valid } = await step1FormRef.value.validate()
  if (!valid) return

  transitionName.value = 'slide-left'
  isSearching.value = true
  currentStep.value = 2

  // Reset selection state
  selectedPlace.value = null
  noneSelected.value = false
  googlePlacesResults.value = []
  existingRestaurants.value = []
  existingPlacesMap.value = {}

  try {
    const service = new GooglePlacesService()
    const searchQuery = `${formData.name} ${formData.market}`

    // Use autocomplete to search for restaurants
    const predictions = await service.getAutocompletePredictions(searchQuery, {
      types: ['restaurant', 'bar', 'cafe', 'food']
    })

    // Get details for each prediction to get photos and full address
    const detailedResults = await Promise.all(
      predictions.slice(0, 5).map(async prediction => {
        try {
          const details = await service.getPlaceDetails(prediction.place_id, [
            'place_id',
            'name',
            'photos',
            'formatted_address',
            'vicinity',
            'geometry',
            'website',
            'formatted_phone_number',
            'opening_hours',
            'address_components',
            'url'
          ])

          return {
            ...details,
            photoUrl: details.photos?.[0]
              ? service.getPhotoUrl(details.photos[0].photo_reference, 100, 100)
              : null
          }
        } catch (err) {
          console.error('Error getting place details:', err)
          
          return {
            place_id: prediction.place_id,
            name: prediction.structured_formatting?.main_text || prediction.description,
            formatted_address: prediction.structured_formatting?.secondary_text || '',
            photoUrl: null
          }
        }
      })
    )

    // Check which places already exist in the database
    const placeIds = detailedResults.map(place => place.place_id).filter(Boolean)
    if (placeIds.length > 0) {
      existingPlacesMap.value = await restaurantsStore.checkExistingPlaces(placeIds)

      // Separate results into new and existing restaurants
      const newRestaurants = []
      const existing = []

      detailedResults.forEach(place => {
        if (existingPlacesMap.value[place.place_id]) {
          // Add database info to the place object
          existing.push({
            ...place,
            dbRestaurant: existingPlacesMap.value[place.place_id]
          })
        } else {
          newRestaurants.push(place)
        }
      })

      googlePlacesResults.value = newRestaurants
      existingRestaurants.value = existing
    } else {
      googlePlacesResults.value = detailedResults
      existingRestaurants.value = []
      existingPlacesMap.value = {}
    }
  } catch (error) {
    console.error('Error searching Google Places:', error)
    googlePlacesResults.value = []
    existingRestaurants.value = []
    existingPlacesMap.value = {}
  } finally {
    isSearching.value = false
  }
}

const selectPlace = place => {
  selectedPlace.value = place
  noneSelected.value = false
}

const selectNone = () => {
  selectedPlace.value = null
  noneSelected.value = true
}

const handleImageError = (_event, index) => {
  // Hide broken images for new restaurants
  if (googlePlacesResults.value[index]) {
    googlePlacesResults.value[index].photoUrl = null
  }
}

const handleExistingImageError = (_event, index) => {
  // Hide broken images for existing restaurants
  if (existingRestaurants.value[index]) {
    existingRestaurants.value[index].photoUrl = null
  }
}

// Helper to extract city and state from Google Places address_components
const extractCityState = addressComponents => {
  if (!addressComponents || !Array.isArray(addressComponents)) return null

  let city = null
  let state = null

  for (const component of addressComponents) {
    if (component.types.includes('locality')) {
      city = component.long_name
    } else if (component.types.includes('administrative_area_level_1')) {
      state = component.short_name // Use abbreviation like "CA", "NY"
    }
  }

  if (city && state) {
    return `${city}, ${state}`
  }
  
  return null
}

const goToStep3 = async () => {
  transitionName.value = 'slide-left'

  // If a place was selected, store its full details
  if (selectedPlace.value) {
    selectedPlaceDetails.value = selectedPlace.value

    // Extract city and state from address_components if available
    const extractedMarket = extractCityState(selectedPlace.value.address_components)
    if (extractedMarket) {
      formData.market = extractedMarket
    }

    // Fetch timezone from coordinates if geometry is available
    const geometry = selectedPlace.value.geometry
    if (geometry?.location?.lat && geometry?.location?.lng) {
      try {
        const service = new GooglePlacesService()
        const lat = typeof geometry.location.lat === 'function' ? geometry.location.lat() : geometry.location.lat
        const lng = typeof geometry.location.lng === 'function' ? geometry.location.lng() : geometry.location.lng
        const tzData = await service.getTimeZone(lat, lng)
        if (tzData?.timeZoneId) {
          selectedPlaceTimeZone.value = tzData.timeZoneId
        }
      } catch (err) {
        console.error('Error fetching timezone:', err)
      }
    }
  }

  currentStep.value = 3
}

const onSubmit = async () => {
  // Validate form before submitting
  const { valid } = await step3FormRef.value.validate()
  if (!valid) return

  isSubmitting.value = true

  try {
    // Map form data to restaurant schema
    // If a Google Place was selected, mark as "Member Favorite" (listed), otherwise "Unlisted"
    const restaurantData = {
      name: formData.name,
      market: formData.market,
      status: selectedPlace.value ? 'Member Favorite' : 'Unlisted',
      sourceType: 'Member Favorite',
      sourceEmail: myUser.value?.email || '',
      sourceFullName: myUser.value ? `${myUser.value.firstName} ${myUser.value.lastName}` : '',
      sourceMemberProfileId: myUser.value?.profileId || '',
      profileId: 'auto',
      acceptingReferrals: !!selectedPlace.value,
      contactInfoNotVerified: true
    }

    // If a Google Place was selected, include the place details
    if (selectedPlaceDetails.value) {
      restaurantData.googlePlaceId = selectedPlaceDetails.value.place_id

      // Store the full Google Place details
      restaurantData.googlePlaceDetails = {
        place_id: selectedPlaceDetails.value.place_id,
        name: selectedPlaceDetails.value.name,
        formatted_address: selectedPlaceDetails.value.formatted_address,
        vicinity: selectedPlaceDetails.value.vicinity,
        geometry: selectedPlaceDetails.value.geometry,
        website: selectedPlaceDetails.value.website,
        formatted_phone_number: selectedPlaceDetails.value.formatted_phone_number,
        opening_hours: selectedPlaceDetails.value.opening_hours,
        address_components: selectedPlaceDetails.value.address_components,
        url: selectedPlaceDetails.value.url,
        photos: selectedPlaceDetails.value.photos?.map(photo => ({
          photo_reference: photo.photo_reference,
          height: photo.height,
          width: photo.width,
          html_attributions: photo.html_attributions
        })) || []
      }

      // Use the Google Place name and address if available
      if (selectedPlaceDetails.value.name) {
        restaurantData.name = selectedPlaceDetails.value.name
      }
      if (selectedPlaceDetails.value.formatted_address) {
        restaurantData.address = selectedPlaceDetails.value.formatted_address
      }
      if (selectedPlaceDetails.value.website) {
        restaurantData.website = selectedPlaceDetails.value.website
      }
      if (selectedPlaceDetails.value.formatted_phone_number) {
        restaurantData.phoneNumber = selectedPlaceDetails.value.formatted_phone_number
      }
      if (selectedPlaceDetails.value.opening_hours?.weekday_text) {
        restaurantData.openingHours = selectedPlaceDetails.value.opening_hours.weekday_text
      }

      // Add timezone if fetched
      if (selectedPlaceTimeZone.value) {
        restaurantData.timeZoneIdentifier = selectedPlaceTimeZone.value
      }

      // Extract domain from website URL for email defaults
      const extractDomain = websiteUrl => {
        if (!websiteUrl) return null
        try {
          const url = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`)
          
          return url.hostname.replace(/^www\./, '')
        } catch {
          return null
        }
      }

      const domain = extractDomain(selectedPlaceDetails.value.website)

      // Set team identity defaults
      restaurantData.generalReservationTeamIdentity = `${restaurantData.name} Team`
      restaurantData.privateReservationTeamIdentity = `${restaurantData.name} Team`

      // Set email defaults based on domain
      // RM-189 — appends are [{ name, email }] entries (name optional/internal).
      if (domain) {
        restaurantData.generalReservationEmailAppends = [{ name: '', email: `reservations@${domain}` }, { name: '', email: `info@${domain}` }]
        restaurantData.privateReservationEmailAppends = [{ name: '', email: `events@${domain}` }, { name: '', email: `info@${domain}` }]
      }

      // Set credit card hold defaults
      restaurantData.generalReservationHoldRequired = true
      restaurantData.generalReservationHoldType = 'full'
      restaurantData.generalReservationHoldTaxPercentage = 12
      restaurantData.generalReservationHoldCancellationLimitHours = 24
    }

    // Call the store method to create restaurant
    const newRestaurant = await restaurantsStore.adminNewRestaurant(restaurantData)

    if (newRestaurant) {
      // Auto-favorite the restaurant if user selected a Google Place (not "None of these")
      if (selectedPlaceDetails.value && myUser.value?._id) {
        try {
          await restaurantsStore.favoriteRestaurant(newRestaurant._id, myUser.value._id)
        } catch (favError) {
          console.error('Error auto-favoriting restaurant:', favError)

          // Don't block the success flow if favoriting fails
        }
      }

      emit('onRestaurantAdded', newRestaurant)

      // Show success confirmation
      formSubmitted.value = true

      console.log('Restaurant added successfully:', newRestaurant)
    }
  } catch (error) {
    console.error('Error submitting restaurant:', error)
    alert('Failed to add restaurant. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.dialog-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.dialog-content {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 24px !important;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--ds-text-strong);
}

.close-button {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.back-button {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.close-button-success {
  position: absolute;
  right: 16px;
  top: 16px;
}

// Step indicator
.step-indicator {
  display: flex;
  justify-content: center;
}

.step-dots {
  display: flex;
  gap: 8px;
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E0E0E0;
  transition: all 0.3s ease;

  &.active {
    background-color: var(--ds-brand-primary-dark);
    width: 24px;
    border-radius: 4px;
  }

  &.completed {
    background-color: var(--ds-brand-primary-dark);
  }
}

// Steps container for animations
.steps-container {
  position: relative;
  min-height: 200px;
}

.step-content {
  width: 100%;
}

// Slide animations
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease-out;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.section-divider {
  border-color: rgba(0, 0, 0, 0.2);
  border-width: 1px;
}

.optional-section-title {
  color: var(--ds-text-muted);
  font-size: 1rem;
  font-weight: 400;
}

.submit-button {
  text-transform: none;
  font-weight: 500;
  height: 48px;
}

.contact-text {
  font-size: 0.875rem;
  color: var(--ds-text-muted);
  text-align: center;
  width: 100%;
  padding-bottom: 8px;
}

.email-link {
  color: var(--ds-brand-primary-dark);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// Google Places results styling
.search-info {
  text-align: center;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.results-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.existing-restaurants-section {
  margin-top: 24px;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding-left: 4px;
}

.place-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid var(--ds-border-default);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--ds-brand-primary-dark);
    background-color: rgba(63, 153, 143, 0.04);
  }

  &.selected {
    border-color: var(--ds-brand-primary-dark);
    background-color: rgba(63, 153, 143, 0.08);
  }

  &.none-option {
    border-style: dashed;
  }

  &.existing-restaurant {
    cursor: default;
    opacity: 0.7;
    background-color: #F5F5F5;
    border-color: var(--ds-border-default);

    &:hover {
      border-color: var(--ds-border-default);
      background-color: #F5F5F5;
    }
  }
}

.place-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #F5F5F5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.none-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--ds-surface-subtle);
    border: 1px dashed var(--ds-border-default);
  }
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #F5F5F5;
  color: var(--ds-text-muted);
}

.place-info {
  flex: 1;
  min-width: 0;
}

.place-name {
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--ds-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.place-address {
  font-size: 0.8rem;
  color: var(--ds-text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.place-status {
  font-size: 0.75rem;
  color: var(--ds-brand-primary-dark);
  margin-top: 4px;
  font-weight: 500;
}

.place-check {
  flex-shrink: 0;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  text-align: center;
}

// Selected restaurant preview in step 3
.selected-restaurant-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: rgba(63, 153, 143, 0.08);
  border: 1px solid rgba(63, 153, 143, 0.3);
  border-radius: 12px;
}

.preview-image {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #F5F5F5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.preview-info {
  flex: 1;
  min-width: 0;
}

.preview-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--ds-text-strong);
}

.preview-address {
  font-size: 0.75rem;
  color: var(--ds-text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .dialog-card {
    max-height: 95vh;
  }

  .dialog-title {
    font-size: 1.1rem;
  }

  .dialog-content {
    padding: 16px !important;
  }

  .place-image {
    width: 50px;
    height: 50px;
  }

  .place-name {
    font-size: 0.9rem;
  }

  .place-address {
    font-size: 0.75rem;
  }
}
</style>
